import { useCallback, useEffect } from 'react';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';

import { useTrackPublisherEvents } from '@/api/useTrackPublisherEvents';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  EEventsType,
  ELocalStorageKeys,
  ELoginMode,
  EPublisherEvents,
  ESessionStorageKeys,
  ESource
} from '@/lib/enums';
import { enrichOptions, getLoginEventsData, getSessionMetadataSource } from '@/lib/eventDataUtils';
import { trackMixpanelEvent } from '@/lib/mixpanelClient';
import { getRedirectPath } from '@/lib/pageRedirectUtils';
import { mapFontWeight } from '@/lib/utils';
import { useAppState } from '@/state/hooks/app.state.hook';
import { useLoginState } from '@/state/hooks/login.state.hook';
import { webStorageUtil } from '@/state/webStorage.state.service';

import { LoadingSpinner } from '../../components/LoadingSpinner';
import { OtpLogin } from '../Modes/OtpLogin';
// Import the new components
import { LoginIdMode } from '../Modes/loginId';
import { UsernamePasswordMode } from '../Modes/username';
import { LoginMethodSelector } from './LoginMethodSelector';
import { useTranslations } from 'next-intl';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';

export const LoginModal: React.FC = () => {
  const t = useTranslations();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const proofKey = searchParams.get('proofKey');
  const token = searchParams.get('token');

  const {
    isLoginModalOpen,
    setIsLoginModalOpen,
    isLoggingIn,
    setIsLoggingIn,
    loginType,
    setLoginType,
    isConsentChecked,
    setIsConsentChecked,
    isOtpRedirectPending,
    isOtpLoggingIn
  } = useLoginState();
  const { initialBoot, platform } = useAppState();
  const { trackPublisherPreLoginEvent } = useTrackPublisherEvents();

  const htmlText = initialBoot?.theme?.loginPopup?.consentSection?.htmlText;
  const consentRequired = initialBoot?.theme?.loginPopup?.consentSection?.consentRequired;

  const trackLoginEvents = useCallback(
    (
      mixpanelEventName: EEventsType,
      publisherEventName: EPublisherEvents,
      additionalData?: Record<string, any>
    ) => {
      trackMixpanelEvent(mixpanelEventName, getLoginEventsData({ platform, ...additionalData }));
      trackPublisherPreLoginEvent.mutate({
        ...enrichOptions({
          platform,
          eventName: publisherEventName,
          method: loginType,
          playerSession: { sessionMetadata: { source: getSessionMetadataSource(pathname) } },
          ...additionalData
        })
      });
    },
    [platform, loginType, pathname]
  );

  useEffect(() => {
    if (!isLoginModalOpen) {
      setLoginType(null);
      setIsLoggingIn(false);
    } else {
      trackLoginEvents(EEventsType.PORTAL_LOGIN_DISPLAYED, EPublisherEvents.PORTAL_LOGIN_DISPLAYED);
    }
  }, [isLoginModalOpen]);

  useEffect(() => {
    if (proofKey && initialBoot && token) {
      setLoginType(ELoginMode.OTP);
    }
  }, [proofKey, token, initialBoot]);

  useEffect(() => {
    if (loginType !== null) {
      trackLoginEvents(
        EEventsType.PORTAL_LOGIN_METHOD_SELECTED,
        EPublisherEvents.PORTAL_LOGIN_METHOD_SELECTED,
        {
          method: loginType
        }
      );
    }
  }, [loginType]);

  const handleConsentChange = (checked: boolean) => {
    setIsConsentChecked(!!checked);
    if (checked) {
      webStorageUtil.set(ELocalStorageKeys.USER_LOGIN_TERMS_CONSENT, JSON.stringify(true));
    } else {
      webStorageUtil.remove(ELocalStorageKeys.USER_LOGIN_TERMS_CONSENT);
    }
  };

  const handleToggleLoginModal = (isOpen: boolean) => {
    if (pathname.includes('store') && !isOtpRedirectPending) {
      const redirectPath = getRedirectPath(initialBoot?.pages);
      router.push(redirectPath);
    }
    setIsLoginModalOpen(isOpen);
    if (!isOpen) {
      trackLoginEvents(EEventsType.PORTAL_LOGIN_CANCELED, EPublisherEvents.PORTAL_LOGIN_CANCELED);
    }
  };

  return (
    <Dialog
      open={isLoginModalOpen && !isOtpLoggingIn && !initialBoot?.settings?.hideAllLoginMethods}
      onOpenChange={(isOpen) => handleToggleLoginModal(isOpen)}
    >
      <DialogContent
        data-testid="PortalLoginModal-Content"
        className="LoginModal fixed flex max-h-[90vh] w-[calc(100%-48px)] max-w-[384px] flex-col gap-y-6 overflow-y-auto !rounded-xl px-8 pb-10 pt-6 text-white outline-none"
        style={{
          background: `url(${initialBoot?.theme?.loginPopup?.backgroundImageDesktop || ''})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          color: initialBoot?.theme?.loginPopup?.fontColor || '#FFF',
          borderColor: initialBoot?.theme?.loginPopup?.fontColor || '#FFF',
          fontFamily: initialBoot?.theme?.loginPopup?.font || 'Roboto, sans-serif',
        }}
      >
        <DialogHeader className="flex h-auto flex-col items-center justify-center gap-y-3">
          <img
            src={initialBoot?.theme?.loginPopup?.logo || ''}
            alt="Logo"
            className="h-[60px] w-auto"
            data-testid="PortalLoginModal-Logo"
          />
          <DialogTitle
            className="!mt-0 text-center"
            data-testid="PortalLoginModal-Title"
            style={{
              fontWeight: mapFontWeight(initialBoot?.theme?.loginPopup?.fontWeight),
              fontSize: initialBoot?.theme?.loginPopup?.fontSize || '16px',
            }}
          >
            {initialBoot?.theme?.loginPopup?.text}
          </DialogTitle>
        </DialogHeader>

        {loginType === null && <LoginMethodSelector />}

        {loginType === ELoginMode.TOKEN_ID && !isLoggingIn && <LoginIdMode />}

        {loginType === ELoginMode.OTP && <OtpLogin />}

        {loginType === ELoginMode.USERNAME && <UsernamePasswordMode />}

        {isLoggingIn && <LoadingSpinner text={t('portal.login.loggingIn')} />}

        <div data-testid="PortalLoginModal-ConsentSection" className="flex justify-center gap-x-2">
          {consentRequired && (
            <Checkbox
              className="mt-0.5"
              checked={isConsentChecked}
              onCheckedChange={(checked) => handleConsentChange(!!checked)}
            />
          )}
          <div
            className="prose !mt-0 min-w-0 break-words text-center text-sm leading-5"
            data-testid="PortalLoginModal-ConsentSection-Text"
            style={{ color: initialBoot?.theme?.loginPopup?.fontColor || '#FFF' }}
          >
            <ReactMarkdown
              remarkPlugins={[remarkBreaks]}
              components={{
                a: ({ node, ...props }) => (
                  <a
                    {...props}
                    style={{ color: initialBoot?.theme?.loginPopup?.fontColor || '#FFF' }}
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                ),
                strong: ({ node, ...props }) => (
                  <strong
                    {...props}
                    style={{ color: initialBoot?.theme?.loginPopup?.fontColor || '#FFF' }}
                  />
                ),
              }}
            >
              {htmlText}
            </ReactMarkdown>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
